import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Sale } from '@/api/saleApi';
import { formatCurrency, formatDate } from '@/utils/helpers';
import SaleStatusBadge from './SaleStatusBadge';

interface SaleDetailDrawerProps {
  open: boolean;
  sale: Sale | null;
  isLoading: boolean;
  error: string;
  canCancel: boolean;
  onClose: () => void;
  onRetry: () => void;
  onCancel: (sale: Sale) => void;
}

const safeFormatDate = (value: string): string => {
  if (!value || Number.isNaN(new Date(value).getTime())) return '—';
  return formatDate(value);
};

const SaleDetailDrawer: React.FC<SaleDetailDrawerProps> = ({
  open,
  sale,
  isLoading,
  error,
  canCancel,
  onClose,
  onRetry,
  onCancel,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, open]);

  if (!open) return null;

  const customerName = sale?.customer?.name?.trim() || t('admin.sales_history.walk_in_customer');

  return (
    <div className="fixed inset-0 z-[100] bg-black/45" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="ml-auto flex h-full w-full max-w-[760px] flex-col bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="sale-detail-title">
        <header className="flex items-start justify-between gap-3 border-b border-outline-variant px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <p className="font-label-sm text-primary">{t('admin.sales_history.detail.eyebrow')}</p>
            <h2 id="sale-detail-title" className="mt-1 truncate font-h2 text-h2 text-on-surface">
              {sale?.code || t('admin.sales_history.detail.title')}
            </h2>
          </div>
          <button type="button" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-secondary hover:bg-surface-container-low" aria-label={t('admin.sales_history.actions.close')} onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          {isLoading && (
            <div className="space-y-4" role="status" aria-label={t('admin.sales_history.detail.loading')}>
              <div className="h-24 animate-pulse rounded-2xl bg-surface-container-low" />
              <div className="h-36 animate-pulse rounded-2xl bg-surface-container-low" />
              <div className="h-56 animate-pulse rounded-2xl bg-surface-container-low" />
            </div>
          )}

          {!isLoading && error && (
            <div className="flex min-h-72 flex-col items-center justify-center text-center" role="alert">
              <span className="material-symbols-outlined text-5xl text-error">cloud_off</span>
              <p className="mt-3 font-h3 text-h3 text-on-surface">{t('admin.sales_history.detail.error_title')}</p>
              <p className="mt-1 text-body-md text-error">{error}</p>
              <button type="button" className="mt-4 rounded-xl bg-primary px-4 py-2 font-label-md text-on-primary" onClick={onRetry}>{t('admin.sales_history.actions.retry')}</button>
            </div>
          )}

          {!isLoading && !error && sale && (
            <div className="space-y-5">
              <section className="grid grid-cols-1 gap-3 rounded-2xl border border-outline-variant/70 bg-surface-container-lowest p-4 sm:grid-cols-2">
                <div>
                  <p className="font-label-sm text-secondary">{t('admin.sales_history.detail.status')}</p>
                  <div className="mt-1"><SaleStatusBadge status={sale.status} /></div>
                </div>
                <div>
                  <p className="font-label-sm text-secondary">{t('admin.sales_history.detail.created_at')}</p>
                  <p className="mt-1 text-on-surface">{safeFormatDate(sale.createdAt)}</p>
                </div>
                <div>
                  <p className="font-label-sm text-secondary">{t('admin.sales_history.detail.employee')}</p>
                  <p className="mt-1 font-medium text-on-surface">{sale.createdBy.username}</p>
                </div>
                <div>
                  <p className="font-label-sm text-secondary">{t('admin.sales_history.detail.sale_id')}</p>
                  <p className="mt-1 font-mono text-on-surface">#{sale.id}</p>
                </div>
              </section>

              <section className="rounded-2xl border border-outline-variant/70 p-4">
                <h3 className="font-h3 text-h3 text-on-surface">{t('admin.sales_history.detail.customer')}</h3>
                <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div><dt className="font-label-sm text-secondary">{t('admin.sales_history.detail.customer_name')}</dt><dd className="mt-1 text-on-surface">{customerName}</dd></div>
                  <div><dt className="font-label-sm text-secondary">{t('admin.sales_history.detail.phone')}</dt><dd className="mt-1 text-on-surface">{sale.customer?.phone?.trim() || '—'}</dd></div>
                  <div className="sm:col-span-2"><dt className="font-label-sm text-secondary">{t('admin.sales_history.detail.address')}</dt><dd className="mt-1 text-on-surface">{sale.customer?.address?.trim() || '—'}</dd></div>
                </dl>
              </section>

              <section className="overflow-hidden rounded-2xl border border-outline-variant/70">
                <div className="border-b border-outline-variant bg-surface-container-lowest px-4 py-3">
                  <h3 className="font-h3 text-h3 text-on-surface">{t('admin.sales_history.detail.products')}</h3>
                </div>
                <div className="divide-y divide-outline-variant/60">
                  {sale.items.map((saleItem) => (
                    <article key={saleItem.id} className="p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="font-medium text-on-surface">{saleItem.productName}</p>
                          <p className="mt-1 text-body-sm text-secondary">{[saleItem.versionName, saleItem.color].filter(Boolean).join(' · ')}</p>
                          <p className="mt-1 text-body-sm text-secondary">{t('admin.sales_history.detail.quantity_price', { quantity: saleItem.quantity, price: formatCurrency(saleItem.unitPrice) })}</p>
                        </div>
                        <p className="shrink-0 font-mono font-bold text-on-surface">{formatCurrency(saleItem.lineTotal)}</p>
                      </div>
                      {saleItem.productItems.length > 0 && (
                        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {saleItem.productItems.map((productItem) => (
                            <div key={productItem.id} className="rounded-xl bg-surface-container-low px-3 py-2 text-body-sm">
                              {productItem.imei && <p><span className="text-secondary">IMEI:</span> <span className="font-mono text-on-surface">{productItem.imei}</span></p>}
                              {productItem.serialNumber && <p><span className="text-secondary">Serial:</span> <span className="font-mono text-on-surface">{productItem.serialNumber}</span></p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              </section>

              <section className="overflow-hidden rounded-2xl border border-outline-variant/70">
                <div className="border-b border-outline-variant bg-surface-container-lowest px-4 py-3">
                  <h3 className="font-h3 text-h3 text-on-surface">{t('admin.sales_history.detail.payments')}</h3>
                </div>
                <div className="divide-y divide-outline-variant/60">
                  {sale.payments.length === 0 ? (
                    <p className="p-4 text-secondary">{t('admin.sales_history.detail.no_payments')}</p>
                  ) : sale.payments.map((payment) => (
                    <div key={payment.id} className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-[1fr_auto]">
                      <div>
                        <p className="font-medium text-on-surface">{payment.method}</p>
                        <p className="mt-1 text-body-sm text-secondary">{t(`admin.sales_history.payment_status.${payment.status.toLowerCase()}`)}</p>
                        {payment.transactionCode && <p className="mt-1 font-mono text-body-sm text-secondary">{payment.transactionCode}</p>}
                      </div>
                      <p className="font-mono font-bold text-on-surface">{formatCurrency(payment.amount)}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-2xl bg-surface-container-low p-4">
                <dl className="space-y-2">
                  <div className="flex justify-between gap-4"><dt className="text-secondary">{t('admin.sales_history.detail.subtotal')}</dt><dd className="font-mono text-on-surface">{formatCurrency(sale.subtotal)}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-secondary">{t('admin.sales_history.detail.discount')}</dt><dd className="font-mono text-on-surface">{formatCurrency(sale.discountAmount)}</dd></div>
                  <div className="flex justify-between gap-4 border-t border-outline-variant pt-3"><dt className="font-h3 text-h3 text-on-surface">{t('admin.sales_history.detail.total')}</dt><dd className="font-mono text-h3 font-bold text-primary">{formatCurrency(sale.totalAmount)}</dd></div>
                </dl>
              </section>

              {sale.note?.trim() && (
                <section className="rounded-2xl border border-outline-variant/70 p-4">
                  <h3 className="font-label-md text-on-surface">{t('admin.sales_history.detail.note')}</h3>
                  <p className="mt-2 whitespace-pre-wrap text-body-md text-secondary">{sale.note}</p>
                </section>
              )}
            </div>
          )}
        </div>

        {!isLoading && !error && sale && (
          <footer className="flex flex-col-reverse gap-2 border-t border-outline-variant bg-surface-container-lowest px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
            <button type="button" className="min-h-10 rounded-xl border border-outline-variant bg-white px-4 font-label-md text-secondary" onClick={onClose}>{t('admin.sales_history.actions.close')}</button>
            {canCancel && sale.status === 'PAID' && (
              <button type="button" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-error px-4 font-label-md text-on-error" onClick={() => onCancel(sale)}>
                <span className="material-symbols-outlined text-[19px]" aria-hidden="true">cancel</span>
                {t('admin.sales_history.actions.cancel_sale')}
              </button>
            )}
          </footer>
        )}
      </section>
    </div>
  );
};

export default SaleDetailDrawer;
